import React, { useRef, useState, useMemo } from "react";

// (2026-10) Vista en gráfica de Procesos Pendientes: prendas pendientes de
// entrega por taller, de mayor a menor. Recibe los mismos renglones que la
// tabla (ya filtrados), así que fechas, taller, proceso, cliente, vencimiento,
// pendientes y búsqueda aplican igual en las dos vistas.
// (2026-10) Las barras quedaron en un componente genérico (GraficaBarrasPiezas)
// para usarlas también en los recuadros de ListadoOrdenesCorte (por taller y
// por cliente).

const ANCHO_ETIQUETA = 220; // columna con el nombre del taller
const ALTO_RENGLON = 30;
const GROSOR_BARRA = 18;

const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));

// pasos "redondos" para el eje: 1, 2, 2.5, 5 × 10^n (nunca menos de 1 pieza)
export const pasoBonito = (max) => {
  if (max <= 0) return 1;
  const bruto = max / 4;
  const pot = Math.pow(10, Math.floor(Math.log10(bruto)));
  const f = bruto / pot;
  const n = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return Math.max(1, n * pot);
};

// Prendas pendientes por taller, de mayor a menor. La usan la gráfica en
// pantalla y el PDF, para que los dos digan lo mismo.
export const agrupaPorTaller = (filas, hoy) => {
  const m = new Map();
  (filas || []).forEach((f) => {
    const pend = Number(f.pendientes || 0);
    if (pend <= 0) return;
    const k = f.idMaquilero || f.maquilero || "sin-taller";
    if (!m.has(k)) {
      m.set(k, {
        id: f.idMaquilero || "",
        taller: f.maquilero || "Sin taller",
        pendientes: 0,
        salidas: 0,
        vencidas: 0,
        procesos: new Map(),
      });
    }
    const g = m.get(k);
    g.pendientes += pend;
    g.salidas += 1;
    // misma regla que el filtro "Vencido": compromiso antes de hoy
    if (f.fechaCompromiso && f.fechaCompromiso < hoy) g.vencidas += pend;
    const proc = f.proceso || "Sin proceso";
    g.procesos.set(proc, (g.procesos.get(proc) || 0) + pend);
  });
  return [...m.values()].sort((a, b) => b.pendientes - a.pendientes);
};

// Eje: tope con 10% de aire para que quepa el valor de la barra más larga
export const ejePendientes = (maxDato) => {
  const paso = pasoBonito(maxDato * 1.1);
  const tope = Math.max(paso, Math.ceil((maxDato * 1.1) / paso) * paso);
  const ticks = [];
  for (let v = 0; v <= tope + 1e-9; v += paso) ticks.push(v);
  return { tope, ticks };
};

const estilos = `
.gpt { --surface:#ffffff; --ink:#0b0b0b; --ink-2:#52514e; --muted:#898781;
  --grid:#ebe9e3; --base:#c3c2b7; --serie:#2a78d6; --serie-hover:#1c5cab;
  --critico:#d03b3b; --borde:rgba(11,11,11,0.10); --hover-row:#f5f7fa;
  color: var(--ink); }
.gpt, .gpt * { box-sizing: border-box; }
.gpt-tiles { display:grid; grid-template-columns:repeat(4, minmax(0,1fr)); gap:12px; margin-bottom:14px; }
@media (max-width: 900px) { .gpt-tiles { grid-template-columns:repeat(2, minmax(0,1fr)); } }
.gpt-tile { background:var(--surface); border:1px solid var(--borde); border-radius:10px; padding:12px 16px; }
.gpt-tile-label { font-size:12px; color:var(--ink-2); display:flex; align-items:center; gap:6px; }
.gpt-tile-valor { font-size:28px; font-weight:600; line-height:1.15; margin-top:4px; }
.gpt-tile-pie { font-size:11.5px; color:var(--muted); margin-top:2px; }
.gpt-card { background:var(--surface); border:1px solid var(--borde); border-radius:12px; padding:18px 22px 14px; }
.gpt-titulo { font-size:16px; font-weight:600; }
.gpt-sub { font-size:12px; color:var(--muted); margin-top:2px; margin-bottom:14px; }
.gpt-plot { position:relative; }
.gpt-filas { position:relative; }
.gpt-grid { position:absolute; top:0; bottom:0; width:1px; background:var(--grid); }
.gpt-grid.base { background:var(--base); }
.gpt-fila { display:flex; align-items:center; height:${ALTO_RENGLON}px; cursor:default;
  border-radius:6px; outline:none; position:relative; }
.gpt-fila.clic { cursor:pointer; }
.gpt-fila:hover, .gpt-fila:focus-visible, .gpt-fila.activa { background:var(--hover-row); }
.gpt-fila:focus-visible { box-shadow:0 0 0 2px var(--serie) inset; }
.gpt-nombre { width:${ANCHO_ETIQUETA}px; flex:0 0 ${ANCHO_ETIQUETA}px; padding-right:12px;
  text-align:right; font-size:12.5px; color:var(--ink-2); white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; }
.gpt-pista { position:relative; flex:1 1 auto; height:100%; }
.gpt-barra { position:absolute; left:0; top:50%; transform:translateY(-50%);
  height:${GROSOR_BARRA}px; background:var(--serie); border-radius:0 4px 4px 0;
  transition:width .35s ease, background-color .15s ease; }
.gpt-fila:hover .gpt-barra, .gpt-fila:focus-visible .gpt-barra, .gpt-fila.activa .gpt-barra { background:var(--serie-hover); }
.gpt-valor { position:absolute; top:50%; transform:translateY(-50%); font-size:12px;
  color:var(--ink); white-space:nowrap; font-variant-numeric:tabular-nums; }
.gpt-eje { position:relative; height:22px; margin-left:${ANCHO_ETIQUETA}px; }
.gpt-tick { position:absolute; top:6px; transform:translateX(-50%); font-size:11px;
  color:var(--muted); font-variant-numeric:tabular-nums; }
.gpt-eje-titulo { text-align:center; font-size:11.5px; color:var(--muted);
  margin-left:${ANCHO_ETIQUETA}px; margin-top:2px; }
.gpt-tip { position:absolute; z-index:5; width:250px; pointer-events:none;
  background:var(--surface); border:1px solid var(--borde); border-radius:10px;
  box-shadow:0 8px 24px rgba(15,23,42,0.12); padding:10px 12px; }
.gpt-tip-valor { font-size:20px; font-weight:600; line-height:1.1; }
.gpt-tip-valor small { font-size:12px; font-weight:400; color:var(--ink-2); margin-left:4px; }
.gpt-tip-taller { font-size:12.5px; color:var(--ink-2); margin-top:2px; }
.gpt-tip hr { border:0; border-top:1px solid var(--grid); margin:8px 0; }
.gpt-tip-fila { display:flex; justify-content:space-between; gap:8px; font-size:12px; color:var(--ink-2); margin-top:3px; }
.gpt-tip-fila b { color:var(--ink); font-weight:600; font-variant-numeric:tabular-nums; }
.gpt-tip-key { display:inline-block; width:10px; height:2px; background:var(--serie);
  vertical-align:middle; margin-right:6px; }
.gpt-vacio { text-align:center; color:var(--muted); padding:48px 0; font-size:13px; }
@media (prefers-reduced-motion: reduce) { .gpt-barra { transition:none; } }
`;

const IconoCritico = ({ margen = 0 }) => (
  <i
    className="fas fa-exclamation-triangle"
    style={{ color: "var(--critico)", marginRight: margen }}
    aria-hidden="true"
  ></i>
);

// Barras horizontales de piezas, de mayor a menor, con recuadros arriba.
// - tiles: [{ etiqueta, valor, pie, critico }]
// - datos: [{ id, nombre, valor, secciones: [[{ etiqueta, valor, critico }]] }]
//   (cada sección del tooltip va separada por una línea)
// - onSelect(id) es opcional: sin él los renglones no son clicables.
export function GraficaBarrasPiezas({
  tiles = [],
  titulo,
  subtitulo,
  ejeTitulo,
  unidad,
  vacio,
  datos,
  onSelect,
}) {
  const contRef = useRef(null);
  const [tip, setTip] = useState(null); // { i, x, y }

  const maxDato = datos.length ? datos[0].valor : 0;
  const { tope, ticks } = ejePendientes(maxDato);
  const pct = (v) => (tope > 0 ? (v / tope) * 100 : 0);
  const clicable = typeof onSelect === "function";

  const posTip = (e, i) => {
    const caja = contRef.current?.getBoundingClientRect();
    if (!caja) return;
    let x;
    let y;
    if (e.type === "focus") {
      const fila = e.currentTarget.getBoundingClientRect();
      x = fila.left - caja.left + ANCHO_ETIQUETA + 16;
      y = fila.bottom - caja.top + 4;
    } else {
      x = e.clientX - caja.left + 16;
      y = e.clientY - caja.top + 14;
    }
    // si no cabe a la derecha, se abre hacia la izquierda del puntero
    if (x + 260 > caja.width) x = Math.max(0, x - 250 - 32);
    setTip({ i, x, y });
  };

  const d = tip ? datos[tip.i] : null;

  return (
    <div className="gpt">
      <style>{estilos}</style>

      {tiles.length ? (
        <div className="gpt-tiles">
          {tiles.map((t) => (
            <div className="gpt-tile" key={t.etiqueta}>
              <div className="gpt-tile-label">
                {t.critico ? <IconoCritico /> : null}
                {t.etiqueta}
              </div>
              <div className="gpt-tile-valor">{num(t.valor)}</div>
              <div className="gpt-tile-pie">{t.pie}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="gpt-card">
        <div className="gpt-titulo">{titulo}</div>
        <div className="gpt-sub">{subtitulo}</div>

        {datos.length === 0 ? (
          <div className="gpt-vacio">{vacio}</div>
        ) : (
          <div
            className="gpt-plot"
            ref={contRef}
            onMouseLeave={() => setTip(null)}
          >
            <div className="gpt-filas">
              {/* líneas guía del eje (una por paso, la del 0 un poco más marcada) */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: ANCHO_ETIQUETA,
                  right: 0,
                  pointerEvents: "none",
                }}
              >
                {ticks.map((t) => (
                  <div
                    key={t}
                    className={`gpt-grid${t === 0 ? " base" : ""}`}
                    style={{ left: `${pct(t)}%` }}
                  />
                ))}
              </div>

              {datos.map((x, i) => (
                <div
                  key={x.id || x.nombre}
                  className={`gpt-fila${clicable && x.id ? " clic" : ""}${
                    tip && tip.i === i ? " activa" : ""
                  }`}
                  role={clicable ? "button" : undefined}
                  tabIndex={0}
                  aria-label={`${x.nombre}: ${num(x.valor)} ${unidad}`}
                  onMouseMove={(e) => posTip(e, i)}
                  onFocus={(e) => posTip(e, i)}
                  onBlur={() => setTip(null)}
                  onClick={() => clicable && x.id && onSelect(x.id)}
                  onKeyDown={(e) => {
                    if (clicable && (e.key === "Enter" || e.key === " ") && x.id) {
                      e.preventDefault();
                      onSelect(x.id);
                    }
                  }}
                >
                  <div className="gpt-nombre" title={x.nombre}>
                    {x.nombre}
                  </div>
                  <div className="gpt-pista">
                    <div
                      className="gpt-barra"
                      style={{ width: `${pct(x.valor)}%` }}
                    />
                    <div
                      className="gpt-valor"
                      style={{ left: `calc(${pct(x.valor)}% + 6px)` }}
                    >
                      {num(x.valor)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="gpt-eje">
              {ticks.map((t) => (
                <div key={t} className="gpt-tick" style={{ left: `${pct(t)}%` }}>
                  {num(t)}
                </div>
              ))}
            </div>
            <div className="gpt-eje-titulo">{ejeTitulo}</div>

            {d ? (
              <div className="gpt-tip" style={{ left: tip.x, top: tip.y }}>
                <div className="gpt-tip-valor">
                  {num(d.valor)}
                  <small>{unidad}</small>
                </div>
                <div className="gpt-tip-taller">
                  <span className="gpt-tip-key"></span>
                  {d.nombre}
                </div>
                {(d.secciones || [])
                  .filter((sec) => sec.length)
                  .map((sec, si) => (
                    <React.Fragment key={si}>
                      <hr />
                      {sec.map((r) => (
                        <div key={r.etiqueta} className="gpt-tip-fila">
                          <span>
                            {r.critico ? <IconoCritico margen={4} /> : null}
                            {r.etiqueta}
                          </span>
                          <b>{num(r.valor)}</b>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function GraficaPendientesTaller({ filas, hoy, onSelectTaller, subtitulo }) {
  const grupos = useMemo(() => agrupaPorTaller(filas, hoy), [filas, hoy]);

  const totales = grupos.reduce(
    (t, g) => ({
      pendientes: t.pendientes + g.pendientes,
      salidas: t.salidas + g.salidas,
      vencidas: t.vencidas + g.vencidas,
    }),
    { pendientes: 0, salidas: 0, vencidas: 0 }
  );
  const pctVencidas =
    totales.pendientes > 0
      ? Math.round((totales.vencidas / totales.pendientes) * 100)
      : 0;

  const datos = grupos.map((g) => ({
    id: g.id,
    nombre: g.taller,
    valor: g.pendientes,
    secciones: [
      [
        { etiqueta: "Salidas con pendiente", valor: g.salidas },
        { etiqueta: "Vencidas", valor: g.vencidas, critico: true },
      ],
      [...g.procesos.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([proc, n]) => ({ etiqueta: proc, valor: n })),
    ],
  }));

  return (
    <GraficaBarrasPiezas
      tiles={[
        {
          etiqueta: "Prendas pendientes",
          valor: totales.pendientes,
          pie: "por entregar de los talleres",
        },
        {
          etiqueta: "Talleres con pendiente",
          valor: grupos.length,
          pie: "con al menos una prenda",
        },
        {
          etiqueta: "Salidas con pendiente",
          valor: totales.salidas,
          pie: "salidas de proceso abiertas",
        },
        {
          etiqueta: "Prendas vencidas",
          valor: totales.vencidas,
          pie: `${pctVencidas}% del pendiente, con fecha compromiso pasada`,
          critico: true,
        },
      ]}
      titulo="Prendas pendientes por taller"
      subtitulo={
        subtitulo ||
        "De mayor a menor · Haz clic en un taller para ver sus salidas en la tabla"
      }
      ejeTitulo="Prendas pendientes de entrega"
      unidad="prendas pendientes"
      vacio="No hay prendas pendientes con los filtros actuales."
      datos={datos}
      onSelect={onSelectTaller}
    />
  );
}

export default GraficaPendientesTaller;
